import {Component, Input, Output, EventEmitter, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterLink} from '@angular/router';
import {PatientsService} from '@shared/api/services/v2/patients.service';
import {UserCareProgramsService} from '@shared/api/services/v2/user-care-programs.service';
import {Patient} from '@shared/api/models/query/patient.model';
import {Pagination} from '@models/pagination.model';
import {MaskedFieldComponent} from '@shared/components/masked-field/masked-field.component';

/**
 * Header shared by the patient overview and its four daily/weekly detail pages, so the
 * identity row (avatar, name, phone, diagnosis/role pills) is identical everywhere.
 *
 * By default it loads the patient and care programme itself. A host that already has them
 * (the overview) can pass `patient` and/or `careProgramTitle` to skip those requests;
 * `careProgramTitle` is `null` while the host is still loading it.
 */
@Component({
    selector: 'cdss-patient-detail-header',
    standalone: true,
    imports: [CommonModule, RouterLink, MaskedFieldComponent],
    templateUrl: './patient-detail-header.component.html',
    styleUrl: './patient-detail-header.component.scss'
})
export class PatientDetailHeaderComponent implements OnInit {

    @Input() patientId!: number | string;
    /** Last breadcrumb crumb — the name of the page hosting the header. */
    @Input() currentLabel = 'Patient Overview';
    @Input() patient: Patient | null = null;
    /** Combined "<diagnosis> - <role>" care programme title; `undefined` = load it here. */
    @Input() careProgramTitle: string | null | undefined = undefined;
    @Output() goBack = new EventEmitter<void>();

    /** Emits the loaded patient once available, so a host page can read `carer` /
     *  `user.phoneNumber` (e.g. to decide whether to show a patient/carer toggle) without
     *  fetching the same record a second time. */
    @Output() patientLoaded = new EventEmitter<Patient>();

    loading = true;
    loadError = false;
    private loadedCareProgramTitle: string | null = null;

    constructor(
        private patientsService: PatientsService,
        private userCareProgramsService: UserCareProgramsService
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            if (!this.patient) {
                this.patient = await this.patientsService.getById(this.patientId as number);
                this.patientLoaded.emit(this.patient);
            }
        } catch {
            this.loadError = true;
        } finally {
            this.loading = false;
        }
        if (this.patient && this.careProgramTitle === undefined) {
            this.loadCareProgramTitle();
        }
    }

    private loadCareProgramTitle(): void {
        const pagination = new Pagination();
        pagination.filter = {user: this.targetUserId};
        this.userCareProgramsService.listPaginated(pagination).subscribe({
            next: response => this.loadedCareProgramTitle = response?.data?.[0]?.careProgram?.title ?? null,
            // The pills are decorative; a failed lookup just leaves them out.
            error: () => this.loadedCareProgramTitle = null
        });
    }

    private get isCarerOnly(): boolean {
        return this.patient?.user?.phoneNumber == null;
    }

    private get person() {
        return this.isCarerOnly ? this.patient?.carer?.user : this.patient?.user;
    }

    get displayName(): string {
        if (!this.patient) return '';
        return `${this.person?.firstName ?? ''} ${this.person?.lastName ?? ''}`.trim();
    }

    get initials(): string {
        return `${this.person?.firstName?.charAt(0) ?? ''}${this.person?.lastName?.charAt(0) ?? ''}`;
    }

    get targetUserId(): number | undefined {
        return this.person?.id;
    }

    get displayPhone(): string {
        return this.person?.phoneNumber ?? '';
    }

    // The API only exposes one combined "<diagnosis> - <role>" string
    // (e.g. "Avoidant/Restrictive Food Intake Disorder (ARFID) - Carers"); split on the
    // last " - ". With no separator the whole string is the diagnosis pill.
    private get title(): string | null {
        return (this.careProgramTitle === undefined ? this.loadedCareProgramTitle : this.careProgramTitle) || null;
    }

    get careProgramDiagnosis(): string | null {
        const title = this.title;
        if (!title) return null;
        const separatorIndex = title.lastIndexOf(' - ');
        return separatorIndex === -1 ? title : title.substring(0, separatorIndex);
    }

    get careProgramRole(): string | null {
        const title = this.title;
        if (!title) return null;
        const separatorIndex = title.lastIndexOf(' - ');
        return separatorIndex === -1 ? null : title.substring(separatorIndex + 3);
    }
}
